import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  SearchIcon, 
  UserIcon, 
  LaptopIcon, 
  ArrowLeftIcon,
  LockIcon,
  UnlockIcon,
  MapPinIcon,
  ClockIcon,
  FilterIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerDevices() {
  const navigate = useNavigate();
  const { customerId } = useParams();
  
  // Customer search state
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Devices state
  const [devicesData, setDevicesData] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [deviceSearch, setDeviceSearch] = useState('');
  
  // Last Active filter state
  const [lastActivePreset, setLastActivePreset] = useState('any');
  const [lastActiveFrom, setLastActiveFrom] = useState('');
  const [lastActiveTo, setLastActiveTo] = useState('');

  // Fetch customers based on search
  const searchCustomers = useCallback(async (query) => {
    try {
      setSearchLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/customers/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to search customers');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Fetch devices for selected customer
  const fetchCustomerDevices = useCallback(async (custId, status, search) => {
    try {
      setDevicesLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Build params object
      const params = { 
        status: status !== 'ALL' ? status : undefined,
        search: search || undefined
      };
      
      // Add Last Active filter params
      if (lastActivePreset !== 'any') {
        params.lastActivePreset = lastActivePreset;
        
        if (lastActivePreset === 'custom') {
          if (lastActiveFrom) params.lastActiveFrom = lastActiveFrom;
          if (lastActiveTo) params.lastActiveTo = lastActiveTo;
        }
      }
      
      const response = await axios.get(`${API}/admin/customers/${custId}/devices`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevicesData(response.data);
      setSelectedCustomer(response.data.customer);
    } catch (error) {
      toast.error('Failed to load customer devices');
    } finally {
      setDevicesLoading(false);
    }
  }, [lastActivePreset, lastActiveFrom, lastActiveTo]);

  // Initial load - search for all customers
  useEffect(() => {
    searchCustomers('');
  }, [searchCustomers]);

  // If customerId is in URL, load that customer's devices
  useEffect(() => {
    if (customerId) {
      fetchCustomerDevices(customerId, statusFilter, deviceSearch);
    }
  }, [customerId, fetchCustomerDevices, statusFilter, deviceSearch]);

  // Debounced customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchCustomers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    navigate(`/customer-devices/${customer.id}`);
  };

  const handleBackToSearch = () => {
    setSelectedCustomer(null);
    setDevicesData(null);
    navigate('/customer-devices');
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      LOCKED: 'bg-orange-100 text-orange-800',
      STOLEN: 'bg-red-100 text-red-800',
      OVERDUE: 'bg-yellow-100 text-yellow-800',
      BLACKLISTED: 'bg-gray-100 text-gray-800',
      SECURITY_ALERT: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return 'Invalid date';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0'); // 0-based
    const year = d.getFullYear();

    // If you only want the date:
    // return `${day}-${month}-${year}`;

    // If you also want time (hh:mm):
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Customer Search View
  if (!customerId) {
    return (
      <div data-testid="customer-devices-search-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Customer Devices</h1>
          <p className="text-gray-600 mt-2">Search for a customer to view their devices</p>
        </div>

        {/* Customer Search */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <SearchIcon className="h-5 w-5 mr-2" />
              Search Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="customer-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customer Results */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchLoading ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              Searching...
            </div>
          ) : customers.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No customers found
            </div>
          ) : (
            customers.map((customer) => (
              <Card 
                key={customer.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-primary"
                onClick={() => handleCustomerSelect(customer)}
                data-testid={`customer-card-${customer.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                        <UserIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                        {customer.email && (
                          <p className="text-sm text-gray-500">{customer.email}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <LaptopIcon className="h-3 w-3" />
                      {customer.deviceCount || 0}
                    </Badge>
                  </div>
                  {customer.phone && (
                    <p className="text-sm text-gray-500 mt-2">{customer.phone}</p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  // Customer Devices View
  return (
    <div data-testid="customer-devices-list-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={handleBackToSearch} 
        className="mb-4"
        data-testid="back-to-search-button"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to Customer Search
      </Button>

      {/* Customer Header */}
      {selectedCustomer && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                  <UserIcon className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{selectedCustomer.name}</h1>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                    {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                    {selectedCustomer.phone && <span>• {selectedCustomer.phone}</span>}
                  </div>
                </div>
              </div>
              {devicesData && (
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary">{devicesData.totalDevices}</p>
                  <p className="text-sm text-gray-500">Total Devices</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Summary */}
      {devicesData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="cursor-pointer hover:border-green-500" onClick={() => setStatusFilter('ACTIVE')}>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">{devicesData.statusCounts.ACTIVE}</p>
              <p className="text-sm text-gray-500">Active</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-orange-500" onClick={() => setStatusFilter('LOCKED')}>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{devicesData.statusCounts.LOCKED}</p>
              <p className="text-sm text-gray-500">Locked</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-red-500" onClick={() => setStatusFilter('STOLEN')}>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-600">{devicesData.statusCounts.STOLEN}</p>
              <p className="text-sm text-gray-500">Stolen</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-yellow-500" onClick={() => setStatusFilter('OVERDUE')}>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{devicesData.statusCounts.OVERDUE}</p>
              <p className="text-sm text-gray-500">Overdue</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-gray-500" onClick={() => setStatusFilter('ALL')}>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-gray-600">{devicesData.totalDevices}</p>
              <p className="text-sm text-gray-500">All</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            {/* First row: Search and Status */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search devices by ID, serial, or model..."
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  className="pl-10"
                  data-testid="device-search-input"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <FilterIcon className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="LOCKED">Locked</SelectItem>
                    <SelectItem value="STOLEN">Stolen</SelectItem>
                    <SelectItem value="OVERDUE">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Second row: Last Active Filter */}
            <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-64">
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                Last active
              </p>
              <Select
                value={lastActivePreset}
                onValueChange={(value) => {
                  setLastActivePreset(value);
                  if (value !== 'custom') {
                    setLastActiveFrom('');
                    setLastActiveTo('');
                  }
                }}
              >
                <SelectTrigger data-testid="last-active-filter">
                  <SelectValue placeholder="Any time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any time</SelectItem>
                  <SelectItem value="1d">1 day ago</SelectItem>
                  <SelectItem value="2d">2 days ago</SelectItem>
                  <SelectItem value="3d">3 days ago</SelectItem>
                  <SelectItem value="4d">4 days ago</SelectItem>
                  <SelectItem value="5d">5 days ago</SelectItem>
                  <SelectItem value="6d">6 days ago</SelectItem>
                  <SelectItem value="7d">7 days ago</SelectItem>
                  <SelectItem value="14d">14 days ago</SelectItem>
                  <SelectItem value="30d">30 days ago</SelectItem>
                  <SelectItem value="45d">45 days ago</SelectItem>
                  <SelectItem value="60d">60 days ago</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {lastActivePreset === 'custom' && (
              <>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-1">From</p>
                  <Input
                    type="datetime-local"
                    value={lastActiveFrom}
                    onChange={(e) => setLastActiveFrom(e.target.value)}
                    placeholder="From"
                    className="w-full"
                    data-testid="last-active-from"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 mb-1">To</p>
                  <Input
                    type="datetime-local"
                    value={lastActiveTo}
                    onChange={(e) => setLastActiveTo(e.target.value)}
                    placeholder="To"
                    className="w-full"
                    data-testid="last-active-to"
                  />
                </div>
              </>
            )}
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Devices Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <LaptopIcon className="h-5 w-5 mr-2" />
            Devices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading devices...</div>
          ) : !devicesData || devicesData.devices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No devices found for this customer
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Device</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Serial</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Last Seen</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Location</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devicesData.devices.map((device) => (
                    <tr 
                      key={device.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/devices/${device.id}`)}
                      data-testid={`device-row-${device.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <LaptopIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="font-medium">{device.model}</p>
                            <p className="text-xs text-gray-500">{device.id.substring(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">{device.serialNumber}</td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(device.status)}>
                          {device.status === 'LOCKED' && <LockIcon className="h-3 w-3 mr-1" />}
                          {device.status === 'ACTIVE' && <UnlockIcon className="h-3 w-3 mr-1" />}
                          {device.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {formatDate(device.lastSeenAt)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {device.city || device.lastIp || 'Unknown'}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/devices/${device.id}`);
                          }}
                          data-testid={`view-device-${device.id}`}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
